import { motion } from "framer-motion";
import { ArrowUpRight, Flame, ScrollText, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n";
import { WHATSAPP, scrollToId } from "@/lib/site";
import { Num, Reveal, SectionHead } from "./Primitives";

const PILLAR_ICONS = [ScrollText, Flame, ShieldCheck];
const PILLAR_TARGETS = ["services", "yagya", "dosh"];

export const Pillars = () => {
  const { t } = useLang();
  return (
    <section data-testid="pillars-section" className="mx-auto max-w-[1440px] px-5 sm:px-8 py-24 lg:py-36">
      <SectionHead eyebrow={t.pillars.eyebrow} title={t.pillars.title} testid="pillars" />
      <div className="mt-16 grid md:grid-cols-3 gap-5">
        {t.pillars.items.map((p, i) => {
          const Icon = PILLAR_ICONS[i];
          return (
            <Reveal key={i} delay={i * 0.12}>
              <motion.button whileHover={{ y: -6 }} transition={{ duration: 0.4 }} data-testid={`pillar-card-${i}`}
                onClick={() => scrollToId(PILLAR_TARGETS[i])}
                className="card group w-full text-left p-8 lg:p-10 min-h-[320px] flex flex-col justify-between">
                <div className="flex items-start justify-between">
                  <Icon className="w-9 h-9 text-[color:var(--saffron)]" strokeWidth={1.2} />
                  <Num n={i + 1} />
                </div>
                <div>
                  <h3 className="display text-3xl lg:text-4xl">{p.title}</h3>
                  <p className="mt-3 text-[color:var(--muted)] leading-relaxed">{p.text}</p>
                  <ArrowUpRight className="mt-6 w-5 h-5 text-[color:var(--gold)] transition-transform duration-500 group-hover:rotate-45" />
                </div>
              </motion.button>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const Row = ({ item, i, testid, enquire }) => (
  <Reveal delay={(i % 3) * 0.08}>
    <a href={WHATSAPP} target="_blank" rel="noreferrer" data-testid={`${testid}-${i}`}
      className="row-link group flex gap-6 py-7 border-t border-[color:var(--line)]">
      <Num n={i + 1} />
      <div className="flex-1">
        <h3 className="display text-2xl sm:text-[1.7rem] transition-colors group-hover:text-[color:var(--saffron-soft)]">{item.title}</h3>
        <p className="mt-2 text-sm text-[color:var(--muted)] leading-relaxed">{item.text}</p>
        <span className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.22em] text-[color:var(--gold)] opacity-70 group-hover:opacity-100 transition-opacity">
          {enquire} <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:rotate-45" />
        </span>
      </div>
    </a>
  </Reveal>
);

export const Consultations = () => {
  const { t } = useLang();
  return (
    <section id="services" data-testid="consultations-section" className="mx-auto max-w-[1440px] px-5 sm:px-8 py-24 lg:py-32">
      <SectionHead eyebrow={t.consult.eyebrow} title={t.consult.title} desc={t.consult.desc} testid="consult" />
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10">
        {t.consult.items.map((c, i) => <Row key={i} item={c} i={i} testid="consult-item" enquire={t.consult.enquire} />)}
      </div>
    </section>
  );
};

export const Yagya = () => {
  const { t } = useLang();
  return (
    <section id="yagya" data-testid="yagya-section" className="relative py-24 lg:py-32 bg-[color:var(--surface)] overflow-hidden">
      <div className="ember absolute -left-40 bottom-0 w-[600px] h-[600px]" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
          <SectionHead eyebrow={t.yagya.eyebrow} title={t.yagya.title} desc={t.yagya.desc} testid="yagya" />
        </div>
        <div className="lg:col-span-7">
          {t.yagya.items.map((y, i) => <Row key={i} item={y} i={i} testid="yagya-item" enquire={t.consult.enquire} />)}
        </div>
      </div>
    </section>
  );
};
